/**
 * Letterhead preset templates — Feb 2026.
 *
 * Each preset is a partial `letterhead` blob that gets deep-merged into the
 * lab's current letterhead config when the admin clicks "Apply" in the
 * Templates gallery. Only fields defined here are overridden — anything the
 * admin has customised outside these keys is preserved.
 *
 * Each preset also carries a small `swatches` array purely for the preview
 * chip in the modal — the actual style comes from the blob.
 */

export const LETTERHEAD_PRESETS = [
  {
    id: "minimal",
    name: "Minimal",
    tagline: "Clean · single-line header · quiet colours",
    swatches: ["#111827", "#6B7280", "#F3F4F6"],
    preset: {
      font_family: "sans",
      base_text_size: 11,
      // Zero-noise header — logo left, name center, contact right, all on one row.
      header_left: ["logo"],
      header_center: ["name"],
      header_right: ["contact"],
      slot_layout: {
        header_left: "column",
        header_center: "column",
        header_right: "column",
      },
      slot_align: {
        header_left: "start",
        header_center: "center",
        header_right: "end",
      },
      // Subtle 1 px charcoal line under the header, no other borders.
      primary_header_border_bottom_thickness: 1,
      primary_header_border_style: "solid",
      primary_header_border_color: "#111827",
      primary_header_border_top_thickness: 0,
      header_bg_color: "#ffffff",
      footer_bg_color: "#ffffff",
      icon_color: "#6B7280",
      icon_size: 11,
      text_color: "#111827",
      header_height_mm: 22,
      footer_height_mm: 14,
      // Wipe any per-field overrides so the minimal look is truly minimal.
      field_styles: {},
    },
  },
  {
    id: "classic",
    name: "Classic",
    tagline: "Traditional serif · double rule · centre-heavy",
    swatches: ["#0B3A67", "#B08D3F", "#FBF9F3"],
    preset: {
      font_family: "serif",
      base_text_size: 12,
      header_left: ["logo"],
      header_center: ["name_address"],
      header_right: ["accreditation"],
      slot_align: {
        header_left: "center",
        header_center: "center",
        header_right: "center",
      },
      slot_layout: {
        header_left: "column",
        header_center: "column",
        header_right: "column",
      },
      primary_header_border_top_thickness: 3,
      primary_header_border_bottom_thickness: 1,
      primary_header_border_style: "double",
      primary_header_border_color: "#B08D3F",
      primary_footer_border_top_thickness: 3,
      primary_footer_border_bottom_thickness: 0,
      primary_footer_border_style: "double",
      primary_footer_border_color: "#B08D3F",
      header_bg_color: "#FBF9F3",
      footer_bg_color: "#FBF9F3",
      icon_color: "#B08D3F",
      icon_size: 12,
      text_color: "#0B3A67",
      header_height_mm: 32,
      footer_height_mm: 20,
      field_styles: {},
    },
  },
  {
    id: "modern",
    name: "Modern",
    tagline: "Bold sans · accent block · vibrant footer",
    swatches: ["#0EA5E9", "#0F172A", "#F8FAFC"],
    preset: {
      font_family: "montserrat",
      base_text_size: 11,
      header_left: ["logo", "name"],
      header_center: [],
      header_right: ["phone", "email", "website"],
      slot_layout: {
        header_left: "row",
        header_center: "column",
        header_right: "column",
      },
      slot_gap_x: {
        header_left: 12,
      },
      slot_align: {
        header_left: "center",
        header_center: "center",
        header_right: "end",
      },
      primary_header_border_bottom_thickness: 4,
      primary_header_border_style: "solid",
      primary_header_border_color: "#0EA5E9",
      primary_header_border_top_thickness: 0,
      header_bg_color: "#F8FAFC",
      footer_bg_color: "#0F172A",
      icon_color: "#0EA5E9",
      icon_size: 12,
      text_color: "#0F172A",
      header_height_mm: 26,
      footer_height_mm: 18,
      field_styles: {},
    },
  },
  {
    id: "ayurveda",
    name: "Ayurveda",
    tagline: "Warm earth palette · Sanskrit tagline · herbal accents",
    swatches: ["#5A3E1B", "#CF9226", "#F7EFDC"],
    preset: {
      font_family: "lora",
      base_text_size: 11,
      header_left: ["logo"],
      header_center: ["name", "tagline"],
      header_right: ["accreditation"],
      slot_layout: {
        header_left: "column",
        header_center: "column",
        header_right: "column",
      },
      slot_align: {
        header_left: "center",
        header_center: "center",
        header_right: "center",
      },
      primary_header_border_top_thickness: 2,
      primary_header_border_bottom_thickness: 2,
      primary_header_border_style: "dotted",
      primary_header_border_color: "#CF9226",
      primary_footer_border_top_thickness: 2,
      primary_footer_border_bottom_thickness: 0,
      primary_footer_border_style: "dotted",
      primary_footer_border_color: "#CF9226",
      header_bg_color: "#F7EFDC",
      footer_bg_color: "#F7EFDC",
      icon_color: "#CF9226",
      icon_size: 12,
      text_color: "#5A3E1B",
      header_height_mm: 30,
      footer_height_mm: 20,
      field_styles: {
        name: { family: "cormorant", weight: 700, size: 22, color: "#5A3E1B" },
        tagline: { family: "lora", weight: 400, size: 11, color: "#8A6820" },
      },
    },
  },
];

/**
 * Deep-merge helper — copies every key from preset into base, replacing
 * (not spreading) nested plain-object keys like `slot_align`, `slot_layout`,
 * and `field_styles`. Anything the admin has set outside these keys is
 * preserved so applying a preset is safe.
 */
export function applyPreset(base, preset) {
  const out = { ...(base || {}) };
  Object.keys(preset || {}).forEach((k) => {
    const v = preset[k];
    if (v && typeof v === "object" && !Array.isArray(v)) {
      // Nested object — merge shallowly.
      out[k] = { ...(out[k] || {}), ...v };
    } else {
      out[k] = v;
    }
  });
  return out;
}
